import { getFreshAccessToken } from '../utils/authToken';
import { SessionExpiredError } from '../utils/sessionExpiredError';

export { SessionExpiredError };

const withBearer = (init: RequestInit, token: string): RequestInit => {
  const headers = new Headers(init.headers);
  headers.set('Authorization', `Bearer ${token}`);
  return { ...init, headers };
};

/**
 * An auth failure from the portals backend's protectMiddleware is a 401 with a text/plain
 * body ("Invalid token" / "Unauthorized"). A 401 with a JSON body is NOT that: the Fluvius
 * link endpoint passes the upstream status and JSON body through verbatim
 * (fluviusHandler: res.status(result.status).json(result.data)), so refreshing the user's
 * session would not help and "session expired" would be wrong. A missing content-type is
 * treated as an auth failure.
 */
const isAuthFailure = (response: Response): boolean => {
  if (response.status !== 401) return false;
  const contentType = response.headers?.get('content-type') ?? '';
  return !contentType.toLowerCase().includes('application/json');
};

/**
 * fetch() for authenticated VITE_API_SERVICES (yuso-portals-backend) calls.
 *
 * - Attaches `Authorization: Bearer <token>` from getFreshAccessToken(), which refreshes the
 *   Cognito session ahead of expiry (caller headers are kept; Authorization is overwritten).
 * - On an auth-failure 401 (non-JSON body, see isAuthFailure) it forces ONE refresh and
 *   retries ONCE. If the refresh fails or the retry is again an auth-failure 401, it throws
 *   SessionExpiredError — the caller should offer a new login.
 * - Every other response (ok or not, including a JSON 401) is returned untouched for the
 *   caller to handle.
 *
 * The retry re-sends `init.body`, so only pass bodies that can be sent twice (strings,
 * FormData, Blobs) — not a one-shot ReadableStream.
 */
export const portalsFetch = async (url: string, init: RequestInit = {}): Promise<Response> => {
  const token = await getFreshAccessToken();
  const response = await fetch(url, withBearer(init, token));
  if (!isAuthFailure(response)) return response;

  let retryToken: string;
  try {
    retryToken = await getFreshAccessToken({ isForced: true });
  } catch {
    throw new SessionExpiredError();
  }

  const retry = await fetch(url, withBearer(init, retryToken));
  if (isAuthFailure(retry)) throw new SessionExpiredError();
  return retry;
};

const MAX_ERROR_MESSAGE_LENGTH = 300;

const pickMessage = (data: unknown): string | undefined => {
  if (typeof data === 'string') return data.trim() || undefined;
  if (!data || typeof data !== 'object') return undefined;
  const { error, message } = data as { error?: unknown; message?: unknown };
  if (typeof error === 'string' && error.trim()) return error.trim();
  if (typeof message === 'string' && message.trim()) return message.trim();
  if (error && typeof error === 'object') {
    const nested = (error as { message?: unknown }).message;
    if (typeof nested === 'string' && nested.trim()) return nested.trim();
  }
  return undefined;
};

/**
 * Best-effort human-readable message from an error response, WITHOUT consuming the body
 * (it reads a clone, so the caller can still call response.json()/text()).
 *
 * Understands JSON `{ error: "..." }`, `{ message: "..." }`, `{ error: { message } }` and
 * plain-text bodies (e.g. the portals backend's 401 "Invalid token"). Returns undefined for
 * empty bodies, JSON without a message, and HTML error pages (a proxy's 502 page is not a
 * message to show a user).
 */
export const readErrorMessage = async (response: Response): Promise<string | undefined> => {
  let text: string;
  try {
    text = await response.clone().text();
  } catch {
    return undefined;
  }
  const trimmed = text.trim();
  if (!trimmed) return undefined;

  let message: string | undefined;
  try {
    message = pickMessage(JSON.parse(trimmed));
  } catch {
    const contentType = response.headers?.get('content-type') ?? '';
    if (contentType.includes('text/html') || trimmed.startsWith('<')) return undefined;
    message = trimmed;
  }
  if (!message) return undefined;
  return message.length > MAX_ERROR_MESSAGE_LENGTH ? `${message.slice(0, MAX_ERROR_MESSAGE_LENGTH)}…` : message;
};
